/**
 * campaign.js — campaign and experiment attribution
 *
 * Reads campaign parameters from the URL, keeps them for the session, and
 * attaches them to the cart so they survive into the order. That is the whole
 * job: Product Radar reads them off the order later. Nothing here changes what
 * the visitor sees.
 *
 * Design rules, all deliberate:
 *
 *   - Parameters are *data*, never instructions. Nothing in this file lets a
 *     URL change price, availability, shipping terms or content. A link is not
 *     an authorisation.
 *   - First touch and last touch are both kept. First touch answers "what
 *     brought them here originally", last touch answers "what closed it", and
 *     collapsing them into one number loses the difference.
 *   - The cart is only written when something actually changed, so a visitor
 *     browsing ten pages does not generate ten cart writes.
 *   - Values are length-capped before being sent. A cart attribute is not a
 *     place to accept arbitrary input from a URL.
 */

const STORAGE_KEY = 'theme:attribution';
const MAX_VALUE_LENGTH = 200;

// The only parameters that are ever read. An allowlist rather than "everything
// that looks like a UTM", so a stray query string cannot end up on an order.
const TRACKED = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'pr_campaign',
  'pr_experiment',
  'pr_variant',
];

function readParams() {
  const params = new URLSearchParams(window.location.search);
  const found = {};

  TRACKED.forEach((key) => {
    const value = params.get(key);
    if (value) found[key] = String(value).slice(0, MAX_VALUE_LENGTH);
  });

  return found;
}

function readStored() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch (error) {
    return {};
  }
}

function store(data) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    // Private browsing, or storage disabled. Attribution is best-effort and
    // must never break the storefront.
  }
}

async function writeToCart(attributes) {
  const root = window.Shopify?.routes?.root || '/';

  try {
    await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
  } catch (error) {
    // Attribution is never worth surfacing an error to a shopper over.
  }
}

function init() {
  const incoming = readParams();
  if (Object.keys(incoming).length === 0) return;

  const stored = readStored();
  const now = new Date().toISOString();

  const next = {
    first: stored.first || { ...incoming, at: now, landing: window.location.pathname },
    last: { ...incoming, at: now, landing: window.location.pathname },
  };

  const changed = JSON.stringify(stored.last) !== JSON.stringify(next.last);
  store(next);
  if (!changed) return;

  // Underscore-prefixed cart attributes are hidden from the customer-facing
  // order summary but still readable through the API — which is exactly right
  // for internal identifiers.
  const attributes = {};
  Object.entries(next.first).forEach(([key, value]) => {
    attributes[`_attr_first_${key}`] = value;
  });
  Object.entries(next.last).forEach(([key, value]) => {
    attributes[`_attr_last_${key}`] = value;
  });

  writeToCart(attributes);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export { readParams, readStored };
